// CADASTRO e MINHA CONTA — a mesma tela, com uma flag.
//
// Três pontos do pedido moram aqui, e os três são a mesma tela:
//
//   T1.3  "não dá pra editar senha, cidade nem instituição — campos não clicáveis, não altera
//         nada. E NÃO FICA CLARO COMO ESSES DADOS FORAM PREENCHIDOS."
//   T1.30 "dá pra abrir minha conta, mas não está interativa nem bonita" (o mesmo, no round 2)
//   T1.38 "o sistema de login/minha conta está horizontalizado — poderia ser vertical"
//
// O T1.3 é o mais instrutivo, porque o mecanismo de edição SEMPRE existiu: os campos nascem
// travados e o botão "Editar" os destrava. Ninguém descobriu isso. A tela não dizia que estavam
// travados, não dizia de onde vinham os valores, e o cinza de `disabled` parecia texto de
// exemplo. Um recurso que existe e ninguém encontra é indistinguível de um recurso que não
// existe — e a auditoria registrou as duas vezes que ele não existia.
//
// O conserto não foi acrescentar função nenhuma. Foi dizer, na tela, o que já era verdade.
import { aoFicarInvalido, aoMexer } from "../../components/avisos-em-portugues";
import "./sign-up.scss";

import { useLocation, useNavigate } from "react-router-dom";
import { paraOndeVoltar } from "../login/para-onde-voltar";
import { useEffect, useRef, useState } from "react";
import Nav from "../../components/nav";
import ConfirmPopUp from "../../components/confirm-pop-up";
import { Aviso, useAviso } from "../../components/aviso-da-tela";
import Utils from "../../classes/utils.class";
import { CreateAppUserObj } from "../../api/app-user.class";
import { useGlobalState } from "../../global-state-context";
import Firebase from "../../api/firebase.class";
import { idadePorExtenso, motivoDeRecusa } from "../../api/idade";
import { ROTULO_TIPO_DE_CONTA, TIPOS_DE_CONTA, TipoDeConta } from "../../instituicao/modelo";
import {
  dicaDeSenha,
  limitesDaDataDeNascimento,
  motivoDeRecusaDoAceite,
  recadoDeQuemAceita,
  SENHA,
} from "./aceite-e-idade";
import EscolherEscola from "../../instituicao/escolher-escola";
import { InstituicaoDaBase, pedirAtivacao, solicitarVinculo } from "../../instituicao/api";

// Letras, numeros e a pontuacao aceita. Fora daqui e caractere que o Firebase aceita mas
// que a crianca nao consegue redigitar no celular da escola.
const CARACTERES_DE_SENHA = /^[a-zA-Z0-9!@#$%^&*()\-_=+{}[\]|\\;:'",.<>/?]*$/;

function SignUp({ isEditing }: { isEditing: boolean }): JSX.Element {
  const navigate = useNavigate();
  const local = useLocation();
  const globalState = useGlobalState();

  const [isAwaitingAsyncEvent, setIsAwaitingAsyncEvent] = useState(false);
  const [fields, setFields] = useState<CreateAppUserObj>({
    name: globalState?.appUser?.Name() || "",
    email: globalState?.authUser?.email || "",
    city: globalState?.appUser?.City() || "",
    educationalInstitution:
      globalState?.appUser?.EducationalInstitution() || "",
    birthDate: globalState?.appUser?.BirthDate() || "",
    accountType: globalState?.appUser?.AccountType(),
  });
  const [password, setPassword] = useState("");

  /**
   * O RETRATO DE COMO OS CAMPOS CHEGARAM — `T8.27`, e é o que dá ao "Cancelar" a que voltar.
   *
   * Ele, na letra: *"Depois de destravar, não há 'Cancelar' para desistir."* Sem este retrato, um
   * Cancelar só poderia recarregar a página — e recarregar é um gesto que a pessoa não pediu, com
   * efeito em tudo o mais que estiver na tela.
   *
   * `useRef` e não `useState`: ele nunca muda depois do primeiro render, e guardá-lo em estado
   * convidaria alguém a atualizá-lo no meio da edição, que é justamente o que o Cancelar não pode ter.
   */
  const camposComoChegaram = useRef(fields);

  const [clickedEdit, setClickedEdit] = useState(false);
  const [erroIdade, setErroIdade] = useState<string | null>(null);
  const [erroTipo, setErroTipo] = useState<string | null>(null);
  // T18.9 · a escola escolhida da base oficial; vira o pedido de vínculo ao criar a conta.
  const [escolaDaBase, setEscolaDaBase] = useState<InstituicaoDaBase | null>(null);

  // T8.14 — o aceite comeca DESMARCADO, sempre. Consentimento pre-marcado nao e consentimento,
  // e e o mesmo defeito do "Deixar publico" que vinha marcado numa plataforma de crianca.
  /**
   * T8.31 · O PEDIDO DE EXCLUSÃO DA CONTA, e ele era um `window.confirm()`.
   *
   * O achado nomeia quatro telas — login, recuperação de senha, remover e adicionar página — e esta
   * não estava na lista. Ela apareceu na varredura que fechou aquelas quatro, e é a **ação mais
   * destrutiva do produto inteiro**: deixar um diálogo do navegador exatamente aqui, enquanto as
   * outras sete telas usam o do produto, é o formato de erro que este épico já cometeu quatro vezes —
   * consertar o que a queixa cita e deixar o vizinho.
   */
  const [pedindoParaExcluir, setPedindoParaExcluir] = useState(false);

  // T8.15 · a faixa de aviso desta tela, no lugar dos doze `alert()` que moravam aqui.
  const { aviso, erro, sucesso, limpar: limparAviso } = useAviso();

  /**
   * PEDIR A SENHA PELO DIÁLOGO DO PRODUTO, e devolver o que foi digitado.
   *
   * A promessa existe porque quem chama precisa ESPERAR a resposta — era o que o `prompt()` dava de
   * graça, sendo bloqueante, e é a única coisa boa que ele fazia. O resto do que ele fazia era
   * mostrar a senha na tela, num computador que na escola é compartilhado.
   */
  const [pedidoDeSenha, setPedidoDeSenha] = useState<null | {
    frase: string;
    responder: (senha: string | null) => void;
  }>(null);

  function pedirSenha(frase: string): Promise<string | null> {
    return new Promise((responder) => setPedidoDeSenha({ frase, responder }));
  }

  const [aceitou, setAceitou] = useState(false);
  const [erroAceite, setErroAceite] = useState<string | null>(null);

  // Medido em producao em 25/09: esta aba dizia so "HQ-Lab". Metade do T8.39 e isto, e a outra
  // metade sao as telas que faltam — a varredura e do TS-T8.14.2.
  useEffect(() => {
    document.title = isEditing ? "Meus dados · HQ-Lab" : "Criar conta · HQ-Lab";
    return () => {
      document.title = "HQ-Lab";
    };
  }, [isEditing]);

  useEffect(() => {
    if (!globalState) return;

    if (!isEditing && globalState.authUser?.emailVerified) {
      navigate("/meus-quadrinhos");
    } else if (isEditing && !globalState.authUser?.emailVerified) {
      // T8.24 · o mesmo destino de volta da outra tela de conta. Ver `para-onde-voltar.ts`.
      navigate("/login", {
        replace: true,
        state: paraOndeVoltar(local, !!globalState.authUser),
      });
    }
  }, [globalState, isEditing, navigate, local]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isAwaitingAsyncEvent) return;
    // Aviso da tentativa anterior sai antes desta: dois avisos ao mesmo tempo, um deles vencido, e a
    // pessoa não tem como saber qual é qual.
    limparAviso();
    if (isEditing && !clickedEdit) {
      setClickedEdit(true);
      return;
    }

    // O PORTÃO ETÁRIO (T1.2). Ver `api/idade.ts` para o piso e a razão dele.
    //
    // A conferência acontece aqui, no envio, e não só no `onChange`: o `<input type="date">` pode
    // ser preenchido por colagem, por autocompletar do navegador e por teclado, e nem todos esses
    // caminhos passam por onde a tela valida ao vivo.
    const recusa = motivoDeRecusa(fields.birthDate);
    if (recusa) {
      setErroIdade(recusa);
      return;
    }
    setErroIdade(null);

    // T8.14 — o aceite dos Termos e da Politica. A conferencia acontece AQUI e nao so no
    // `required` da caixa, pelo mesmo motivo do tipo de conta: conta que nasce por provedor
    // externo pula o formulario inteiro, e um `required` de HTML nao guarda esse caminho.
    const recusaDoAceite = motivoDeRecusaDoAceite({ aceitou, editando: isEditing });
    if (recusaDoAceite) {
      setErroAceite(recusaDoAceite);
      return;
    }
    setErroAceite(null);

    // O TIPO DE CONTA E OBRIGATORIO — T2.72, na letra dele: "devia colocar como OBRIGATORIO
    // isso, no minimo se tiver estudante ou e [professor] para poder abrir esse tipo de opcao".
    //
    // A conferencia acontece AQUI, no envio, e nao so no `required` do grupo de radio: o T2.71
    // levanta justamente o caminho que pula a etapa — "se o cara cadastra com o email, o cadastro
    // e meio que automatico, ne, entao nao funciona". Uma conta que nasce por provedor externo
    // nunca passa por este formulario, e para ela a cobranca tem que acontecer DEPOIS, na tela de
    // instituicao. Este bloco fecha o caminho do formulario; o outro esta em `minha-instituicao`.
    if (!fields.accountType) {
      setErroTipo(
        "Escolha se você é estudante ou educador. Isso decide o que você pode fazer numa instituição."
      );
      return;
    }
    setErroTipo(null);

    const _fields = Utils.getTrimmed(fields);
    setFields(_fields);
    const _password = password.trim();
    setPassword(_password);

    setIsAwaitingAsyncEvent(true);
    if (!isEditing) {
      Firebase.createUser(_fields, _password, async (uid) => {
        if (!escolaDaBase) return;
        const pessoa = { uid, nome: _fields.name, email: _fields.email };
        // Educador: pede a ativação (ou entra no pedido de uma escola já ativa). Estudante: pede
        // para entrar — em escola ainda não ativa, o pedido fica guardado até ela ativar (08/10).
        if (_fields.accountType === "educador") await pedirAtivacao(escolaDaBase, pessoa);
        else
          await solicitarVinculo({
            instituicaoId: escolaDaBase.id,
            instituicaoNome: escolaDaBase.nome,
            uid,
            nome: pessoa.nome,
            email: pessoa.email,
            papel: "aluno",
          });
      })
        .then(() => {
          // T8.15 · O AVISO VIAJA COM A PESSOA. Um `alert()` seguido de `navigate` mostra a frase e
          // a joga fora no mesmo gesto — quem fechou o alerta chega ao login sem saber por que está
          // lá. O motivo vai no estado da rota, e a tela de login diz a frase.
          navigate("/login", { replace: true, state: { motivo: "conta-criada" } });
        })
        .catch((err) => {
          console.log(err);
          console.log(err.code);

          // T8.15 · a faixa da tela, no lugar do diálogo do navegador. E as frases passaram a dizer
          // O QUE FAZER: "E-mail inválido" descreve o problema e não a saída.
          switch (err.code) {
            case "auth/invalid-email":
              erro("Esse e-mail não parece certo. Confira se tem @ e se o final está completo.");
              break;
            case "auth/email-already-in-use":
              erro(
                "Já existe uma conta com esse e-mail. Se for a sua, entre pela tela de login — dá " +
                  "para recuperar a senha por lá."
              );
              break;
            case "auth/too-many-requests":
              erro("Você tentou muitas vezes seguidas. Espere alguns minutos e tente de novo.");
              break;
            // T9.2 — A FRASE CULPAVA O MOMENTO. Sem senha, o serviço de contas recusa e a tela
            // dizia "tente de novo em instantes": quem repetiu leu a mesma frase, porque o que
            // faltava não era o instante. O navegador agora barra antes (`required`), e estas
            // duas linhas cobrem o caminho que não passa por ele.
            case "auth/missing-password":
              erro("Falta escolher uma senha.");
              break;
            case "auth/weak-password":
              erro("Essa senha é curta demais. Use pelo menos " + SENHA.minimo + " caracteres.");
              break;
            default:
              erro("Não deu para criar a conta agora. Tente de novo em instantes.");
              break;
          }
        })
        .finally(() => setIsAwaitingAsyncEvent(false));
    } else {
      try {
        if (!!_password) {
          // T8.15 · ERA `prompt()`, e o problema era maior que a identidade visual: `prompt()`
          // MOSTRA o que se digita, e o que se digitava ali era a senha — num computador que na
          // escola é compartilhado. Agora é o campo do produto, com `type="password"`.
          const confirmPassword = await pedirSenha(
            "Para trocar a senha, digite a senha que você usa hoje."
          );
          if (!confirmPassword) return;

          await Firebase.updatePassword(
            globalState!.authUser!,
            confirmPassword,
            _password
          );
          setPassword("");
        }

        await globalState!.appUser!.update(_fields);
        setClickedEdit(false);
        sucesso("Pronto, os seus dados foram salvos.");
        // VOLTA PARA A ÁREA, e isto passou a existir com o PL-T2.12.
        //
        // Antes, `/minha-conta` era este formulário e não havia para onde voltar: salvar deixava
        // a pessoa na mesma tela, com os campos travando de novo, e o único sinal de sucesso era
        // o alerta. Agora o formulário mora em `/minha-conta/editar` e a área que mostra os dados
        // é `/minha-conta` — então salvar termina onde a pessoa vê o que salvou.
        navigate("/minha-conta");
      } catch (err: any) {
        console.log(err);
        console.log(err.code);

        switch (err.code) {
          case "auth/too-many-requests":
            erro("Você tentou muitas vezes seguidas. Espere alguns minutos e tente de novo.");
            break;
          case "auth/invalid-login-credentials":
          case "auth/wrong-password":
            erro("A senha de hoje não está certa. Tente digitar de novo.");
            break;
          default:
            erro("Não deu para salvar os seus dados agora. Tente de novo em instantes.");
            break;
        }
      } finally {
        setIsAwaitingAsyncEvent(false);
      }
    }
  }

  async function handleDeleteAccount() {
    if (isAwaitingAsyncEvent || !globalState?.authUser) return;

    // T8.15 · a senha pelo campo do produto, e não pelo `prompt()` que a mostrava na tela.
    const password = await pedirSenha("Para apagar a conta, digite a sua senha.");
    if (!password) return;



    setIsAwaitingAsyncEvent(true);
    Firebase.deleteUser(globalState?.authUser, password)
      .then(() => {
        // Sem aviso antes de navegar: a conta deixou de existir, e a pessoa vai para a página inicial
        // deslogada. Um aviso que aparece e desaparece no mesmo gesto não é aviso.
        navigate("/", { replace: true });
      })
      .catch((err) => {
        console.log(err);
        console.log(err.code);

        switch (err.code) {
          case "auth/requires-recent-login":
            erro(
              "Por segurança, é preciso ter entrado há pouco para apagar a conta. Saia, entre de " +
                "novo e tente outra vez."
            );
            break;
          case "auth/invalid-login-credentials":
          case "auth/wrong-password":
            erro("A senha não está certa. Tente digitar de novo.");
            break;
          case "auth/too-many-requests":
            erro("Você tentou muitas vezes seguidas. Espere alguns minutos e tente de novo.");
            break;
          default:
            erro("Não deu para apagar a conta agora. Tente de novo em instantes.");
            break;
        }
      })
      .finally(() => setIsAwaitingAsyncEvent(false));
  }

  if (!globalState) return <></>;

  const _clickedEdit = !isEditing || (isEditing && clickedEdit);
  return (
    <div className="sign-up-container">
      <Nav />

      <form onSubmit={handleSubmit} className="conta-form" onInvalidCapture={aoFicarInvalido} onInputCapture={aoMexer} onChangeCapture={aoMexer}>
        {/* ── T12.8 · A VOLTA PARA O RESUMO DA CONTA ─────────────────────────────────
            Quem testou em 28/09, aos 3:13: *"sempre falta aqui apenas um botão de voltar. Abre a
            tela maior de «minha conta»; eu quero voltar para ver as outras coisas de «minha
            conta» e não tem."* O registro mostra o custo: aos 3:37 ele clicou de novo em "Minha
            conta" NO MENU, porque era a única saída.

            São duas telas, e isso é de propósito: `/minha-conta` é o resumo — com os vínculos de
            instituição, a sessão e os dados — e `/minha-conta/editar` é este formulário. O que
            faltava era o caminho de volta entre elas. Só na edição: em "Criar conta" não há
            resumo nenhum para voltar. */}
        {isEditing && (
          <button
            type="button"
            className="conta-voltar"
            onClick={() => navigate("/minha-conta")}
          >
            <span aria-hidden="true">←</span> Minha conta
          </button>
        )}

        <h1>{!isEditing ? "Criar conta" : "Meus dados"}</h1>

        {/* T1.3, na letra: "não fica claro como esses dados foram preenchidos".
            Esta é a frase que responde isso, e ela muda conforme o estado — dizer sempre a mesma
            coisa seria mentir metade do tempo. */}
        {isEditing && (
          <p className="conta-explicacao">
            {clickedEdit ? (
              <>
                Agora dá para mudar. Toque em <strong>Salvar</strong> quando terminar.
              </>
            ) : (
              <>
                Estes são os dados que você preencheu quando criou a conta. Eles ficam
                <strong> travados</strong> para ninguém mudar sem querer — toque em{" "}
                <strong>Editar</strong> para destravar.
              </>
            )}
          </p>
        )}

        {/* Coluna única (T1.38: "poderia ser vertical"). Duas colunas obrigavam o olho a
            ziguezaguear entre campos que não têm relação nenhuma entre si — nome e e-mail,
            nascimento e senha. Formulário curto se lê de cima para baixo. */}
        <div className="conta-campos">
          <div className="campo">
            <label htmlFor="name">Nome completo</label>
            <input
              id="name"
              required
              autoComplete="name"
              disabled={isAwaitingAsyncEvent || !_clickedEdit}
              maxLength={50}
              value={fields.name}
              onChange={(event) =>
                setFields({ ...fields, name: event.target.value.trimStart() })
              }
            />
          </div>

          <div className="campo">
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              required
              type="email"
              autoComplete="email"
              maxLength={50}
              disabled={isAwaitingAsyncEvent || isEditing}
              value={fields.email}
              onChange={(event) =>
                setFields({ ...fields, email: event.target.value.trim() })
              }
            />
            {/* O e-mail é o único campo que continua travado mesmo depois de "Editar", porque
                ele identifica a conta no Firebase. Sem esta linha, o campo parece um defeito. */}
            {isEditing && (
              <span className="campo-dica">
                O e-mail identifica a sua conta e não pode ser trocado aqui.
              </span>
            )}
          </div>

          {/* ── ESTUDANTE OU EDUCADOR (T2.68, T2.72) ──────────────────────────────────────
              "na parte de criar conta vai ter que ter uma questao de tipo estudante ou professor
               e tal, moderador."

              SAO DOIS E NAO TRES, e vale dizer por que: "moderador" nao e tipo de conta — e o
              que um educador se torna DENTRO de uma instituicao, e isso e o `papel` do vinculo
              (T2.66). Tratar como terceiro tipo criaria uma pessoa que e moderadora de nada.

              E o rotulo de educador traz a definicao DELE, do T2.76: "o termo que eu estou usando
              e educador, entao a gente esta falando de professores, diretores, funcionarios, todo
              mundo que trabalha com educacao no contexto daquela instituicao". Sem isso, a
              secretaria da escola leria "educador" e marcaria "estudante".

              `<fieldset>` com `<legend>`, e nao um `<select>`: sao duas opcoes com explicacao, e
              um leitor de tela anuncia "grupo: voce e..." antes de ler as duas. Num `<select>` a
              explicacao nao cabe. */}
          <fieldset className="campo campo-tipo" disabled={isAwaitingAsyncEvent || !_clickedEdit}>
            <legend>Você é...</legend>
            {TIPOS_DE_CONTA.map((t: TipoDeConta) => (
              <label className="campo-radio" key={t}>
                <input
                  type="radio"
                  name="accountType"
                  value={t}
                  required
                  checked={fields.accountType === t}
                  onChange={() => {
                    setFields({ ...fields, accountType: t });
                    setErroTipo(null);
                  }}
                />
                <span>{ROTULO_TIPO_DE_CONTA[t]}</span>
              </label>
            ))}
            <span className="campo-dica">
              Isso decide o que você pode fazer numa instituição de ensino: educador pode cadastrar
              a escola e aceitar quem pede para entrar.
            </span>
            {erroTipo && (
              <span className="campo-erro" role="alert">
                {erroTipo}
              </span>
            )}
          </fieldset>

          {/* T18.9 · a escola vem da BASE OFICIAL do INEP (regras de 06/10, repo
              instituicoes-ensino): ninguém digita o nome. Opcional, porque o vínculo é opcional;
              escolhida aqui, o pedido para entrar nela nasce junto com a conta (08/10, "as duas vias"). */}
          <div className="campo">
            <label htmlFor="educationalInstitution">Escola (opcional)</label>
            <EscolherEscola
              id="educationalInstitution"
              valorAtual={fields.educationalInstitution}
              desabilitado={isAwaitingAsyncEvent || !_clickedEdit}
              aoEscolher={(escola) => {
                setEscolaDaBase(escola);
                if (escola) setFields({ ...fields, educationalInstitution: escola.nome });
              }}
            />
          </div>

          <div className="campo">
            <label htmlFor="city">Cidade</label>
            <input
              id="city"
              required
              autoComplete="address-level2"
              disabled={isAwaitingAsyncEvent || !_clickedEdit}
              maxLength={50}
              value={fields.city}
              onChange={(event) =>
                setFields({ ...fields, city: event.target.value.trimStart() })
              }
            />
          </div>

          <div className="campo">
            {/* Era "D. de Nascimento". A abreviação economizava sete letras e custava a
                compreensão de quem está aprendendo a ler. */}
            <label htmlFor="birthDate">Data de nascimento</label>
            {/* T8.14 — as pontas do calendario saem das MESMAS constantes que o `motivoDeRecusa`
                usa. Antes o `max` era hoje e o `min` era 1900: o campo oferecia data que o envio ia
                recusar, e a pessoa descobria depois de preencher tudo. */}
            <input
              id="birthDate"
              type="date"
              required
              autoComplete="bday"
              disabled={isAwaitingAsyncEvent || !_clickedEdit}
              min={limitesDaDataDeNascimento().min}
              max={limitesDaDataDeNascimento().max}
              value={fields.birthDate}
              aria-describedby="birthDate-dica"
              aria-invalid={!!erroIdade}
              onChange={(event) => {
                setErroIdade(null);
                setFields({ ...fields, birthDate: event.target.value });
              }}
              /* ── QUEM RECUSA FALA, E ANTES DISTO QUEM RECUSAVA ERA MUDO ────────────────────
                 Medido em 28/09 na tela, sem sessão: com uma data abaixo do piso, com uma data do
                 futuro e com um ano de três dígitos, o cadastro NÃO acontecia — e a tela não dizia
                 nada. Nenhuma conta era criada; nenhuma frase aparecia.

                 A causa é a soma de duas decisões certas. O `min`/`max` do calendário (T8.14) existe
                 para o campo não OFERECER data que o envio recusaria; o `motivoDeRecusa` no `submit`
                 (T1.2) existe porque colagem e autocompletar não passam pelo `onChange`. Só que o
                 navegador valida o campo ANTES de chamar o `onSubmit`: fora das pontas, o `submit`
                 nunca acontece, o `motivoDeRecusa` nunca roda, e a frase escrita para uma criança —
                 "Peça para a sua professora criar a sua conta" — nunca é lida. No lugar dela vem a
                 bolha do navegador, em linguagem de navegador, que some sozinha.

                 O `invalid` é o gancho que o navegador dá justamente aqui. `preventDefault()` cala a
                 bolha nativa; a linha seguinte põe a NOSSA frase, com `role="alert"`. O envio continua
                 barrado — não se ganha caminho nenhum, ganha-se a explicação.

                 E ele não substitui a checagem do `submit`: aquela continua sendo a que guarda
                 colagem, autocompletar e conta que nasce por provedor externo. */
              onInvalid={(event) => {
                event.preventDefault();
                setErroIdade(
                  motivoDeRecusa(event.currentTarget.value) ??
                    "Confira a sua data de nascimento."
                );
              }}
            />
            {/* A idade é devolvida em voz alta. Um campo de data preenchido e nunca comentado é
                o que fazia a plataforma "não perguntar idade" mesmo perguntando: a declaração
                precisa ser consciente para ser uma declaração. */}
            <span className="campo-dica" id="birthDate-dica">
              {idadePorExtenso(fields.birthDate)
                ? `Você tem ${idadePorExtenso(fields.birthDate)}.`
                : "A gente usa a sua idade para deixar o HQ-Lab seguro para você."}
            </span>
            {erroIdade && (
              <span className="campo-erro" role="alert">
                {erroIdade}
              </span>
            )}
          </div>

          <div className="campo">
            <label htmlFor="password">{isEditing ? "Nova senha" : "Senha"}</label>
            {/* T9.2 · `minLength` NAO cobre o vazio: em HTML, campo vazio passa no minimo,
                e o cadastro seguia para o servico de contas sem senha nenhuma. Na EDICAO o campo
                continua opcional, porque ali vazio quer dizer "mantenha a senha de sempre" --
                e e a propria dica da tela que diz isso. */}
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              disabled={isAwaitingAsyncEvent || !_clickedEdit}
              required={!isEditing}
              minLength={SENHA.minimo}
              maxLength={SENHA.maximo}
              value={password}
              aria-describedby="password-dica"
              onChange={(event) => {
                const senha = event.target.value.trim();
                if (CARACTERES_DE_SENHA.test(senha)) setPassword(senha);
              }}
            />
            <span className="campo-dica" id="password-dica">
              {isEditing
                ? "Deixe em branco para continuar com a senha de sempre."
                : dicaDeSenha()}
            </span>
          </div>
        </div>

        {/* ── O ACEITE, E ELE FICA ONDE A DECISAO ACONTECE ────────────────────────────────
            Os tres links legais ja existiam no RODAPE, e la ninguem os le ao criar conta. O que
            faltava nao era o texto: era o gesto, no lugar em que a pessoa decide.

            Nao aparece na edicao de dados: quem edita a propria cidade nao esta consentindo de
            novo, e cobrar de novo prenderia a pessoa dentro do proprio cadastro. */}
        {!isEditing && (
          <div className="campo campo-aceite">
            {/* ── QUEM ACEITA, QUANDO QUEM PREENCHE E MENOR (T9.7) ──────────────────────
                Medido em 02/10: a palavra "responsavel" aparecia ZERO vezes nesta tela, e os
                Termos que ela manda aceitar dizem que o uso por menor acontece com
                acompanhamento da escola ou de um responsavel. A tela ja pergunta a idade,
                calcula e diz em voz alta — e nao usava o que descobriu.

                Nao ha campo de responsavel aqui de proposito: pedir nome e contato de um
                adulto por causa de uma crianca e coleta de dado novo, e isso espera o parecer
                juridico que as tres paginas legais tambem esperam. */}
            {recadoDeQuemAceita(fields.birthDate) && (
              <p className="campo-aviso-responsavel" role="note">
                {recadoDeQuemAceita(fields.birthDate)}
              </p>
            )}
            <label className="campo-checkbox">
              <input
                type="checkbox"
                name="aceite"
                required
                checked={aceitou}
                disabled={isAwaitingAsyncEvent}
                aria-describedby="aceite-erro"
                aria-invalid={!!erroAceite}
                onChange={(event) => {
                  setAceitou(event.target.checked);
                  setErroAceite(null);
                }}
              />
              <span>
                Eu li e aceito os{" "}
                <a href="/termos" target="_blank" rel="noreferrer">
                  Termos de uso
                </a>{" "}
                e a{" "}
                <a href="/privacidade" target="_blank" rel="noreferrer">
                  Política de privacidade
                </a>
                .
              </span>
            </label>
            {erroAceite && (
              <span className="campo-erro" role="alert" id="aceite-erro">
                {erroAceite}
              </span>
            )}
          </div>
        )}

        <Aviso aviso={aviso} />

        <div className="conta-acoes">
          <button
            className="conta-botao-principal"
            type="submit"
            disabled={isAwaitingAsyncEvent}
          >
            {/* ── T8.27 · DUAS VEZES O MESMO TEXTO ──────────────────────────────────────────
                Ele, na letra: *"Para editar é preciso clicar em 'Editar meus dados' em Minha conta e
                DE NOVO, COM O MESMO TEXTO, na tela seguinte, para destravar os campos."*

                O botao de Minha conta NAVEGA; este DESTRAVA. Sao duas acoes, e diziam a mesma frase —
                entao o segundo clique parecia o primeiro nao ter funcionado.

                "Editar" tambem e o que a explicacao ao lado ja mandava fazer: *"toque em Editar para
                destravar"*. O botao contrariava o texto que estava do lado dele.

                A TRAVA FICA, e isso e decisao anterior deste epico (T1.3): os campos travados existem
                *"para ninguem mudar sem querer"*. O defeito era a palavra repetida, nao a trava. */}
            {isEditing && !clickedEdit ? "Editar" : "Salvar"}
          </button>

          {/* T8.27 · O CANCELAR, que so existe quando ha o que desistir de. */}
          {isEditing && clickedEdit && (
            <button
              className="conta-botao-secundario"
              type="button"
              disabled={isAwaitingAsyncEvent}
              onClick={() => {
                // Volta ao retrato de como os campos chegaram, e tranca de novo. NAO recarrega a
                // pagina: recarregar e um gesto que a pessoa nao pediu.
                setFields(camposComoChegaram.current);
                setPassword("");
                setErroIdade(null);
                setErroTipo(null);
                setClickedEdit(false);
              }}
            >
              Cancelar
            </button>
          )}
        </div>

        {/* Excluir conta ficava colado no botão de salvar, como um `<span>` clicável — sem Tab,
            sem foco, e a um deslize de distância da ação que a pessoa realmente queria. Agora é
            botão de verdade, atrás de uma régua, no fim da página. */}
        {isEditing && (
          <div className="conta-zona-de-risco">
            <h2>Encerrar</h2>
            <p>Excluir a conta apaga os seus quadrinhos e não tem volta.</p>
            <button
              type="button"
              className="conta-botao-excluir"
              disabled={isAwaitingAsyncEvent}
              onClick={() => setPedindoParaExcluir(true)}
            >
              Excluir minha conta
            </button>
          </div>
        )}
      </form>

      {/* T8.31 · o dialogo do PRODUTO no lugar do `window.confirm()`. O botao nomeia a acao, como nos
          outros tres dialogos que apagam coisa (T8.26) — e "Sim" seria pouco na acao que apaga a
          conta e os quadrinhos de uma crianca. */}
      {pedidoDeSenha && (
        <ConfirmPopUp
          type="input"
          tipoDoCampo="password"
          text={pedidoDeSenha.frase}
          rotuloConfirmar="Continuar"
          close={() => {
            pedidoDeSenha.responder(null);
            setPedidoDeSenha(null);
          }}
          confirm={(senha) => {
            pedidoDeSenha.responder(senha);
            setPedidoDeSenha(null);
          }}
        />
      )}

      {pedindoParaExcluir && (
        <ConfirmPopUp
          type="confirm"
          text={
            "Excluir a sua conta? Os seus quadrinhos são apagados junto, e isto não tem volta."
          }
          rotuloConfirmar="Excluir a minha conta"
          close={() => setPedindoParaExcluir(false)}
          confirm={() => {
            setPedindoParaExcluir(false);
            handleDeleteAccount();
          }}
        />
      )}
    </div>
  );
}

export default SignUp;
